import {
  Box,
  Flex,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  useDisclosure,
  MenuDivider,
  MenuItemProps,
} from '@chakra-ui/react';
import React, {Fragment} from 'react';
import {Link, NavLink, useLocation} from 'react-router-dom';
import {CaretDown, CaretUp} from '@carbon/icons-react';
import {
  useCustomer,
  useIsFeatureEnabled,
  useIsFreemiumV3Customer,
  useIsFreeOrListBuilderCustomer,
  useMarkets,
} from '../../hooks/api/metadata';
import {CreditUsage} from './CreditUsage';
import {AppMenu} from './AppMenu';

const baseStyle = {
  borderRadius: 'md',
  cursor: 'pointer',
  color: 'kgray.50',
  fontWeight: 'normal',
  paddingX: 3,
  paddingY: 2,
  _hover: {
    bg: 'kgray.500',
  },
};

const activeStyle = {
  ...baseStyle,
  bg: 'kgray.500',
  fontWeight: 'medium',
};

const NavItem = ({to, children}: {to: string; children: React.ReactNode}) => {
  return (
    <NavLink
      to={to}
      className={({isActive}) => (isActive ? 'activeNavLink' : undefined)}
    >
      <Flex
        direction="column"
        justify="space-around"
        {...baseStyle}
        sx={{'.activeNavLink &': activeStyle}}
      >
        {children}
      </Flex>
    </NavLink>
  );
};

type Item = {
  label: string;
  path: string;
  children?: {
    label: string;
    path: string;
  }[];
};

const NavDropdownItem = ({label, path, ...props}: Item & MenuItemProps) => {
  return (
    <MenuItem
      key={path}
      as={Link}
      to={path}
      bg="transparent"
      color="kgray.50"
      _hover={{
        textDecoration: 'underline',
        textUnderlineOffset: '6px',
      }}
      px="4"
      py="2"
      {...props}
    >
      {label}
    </MenuItem>
  );
};

const NavDropdown = ({
  label,
  items,
}: {
  label: string;
  items: (Item | 'divider')[];
}) => {
  const location = useLocation();
  const isActive = items.some(
    (item) =>
      item !== 'divider' &&
      (location.pathname.startsWith(item.path) ||
        item.children?.some((child) =>
          location.pathname.startsWith(child.path)
        ))
  );
  const {isOpen, onOpen, onClose} = useDisclosure();

  return (
    <Menu
      isLazy
      placement="bottom"
      autoSelect={false}
      gutter={0}
      isOpen={isOpen}
    >
      <MenuButton
        sx={isActive ? activeStyle : undefined}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        {...baseStyle}
      >
        <Flex align="center" gap="2">
          {label}
          {isOpen ? <CaretUp /> : <CaretDown />}
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList
          bg="kgray.400"
          border="none"
          borderTopRadius="0"
          borderBottomRadius="lg"
          minW="auto"
          p="2"
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
        >
          {items.map((item) =>
            item === 'divider' ? (
              <MenuDivider key={item} />
            ) : (
              <Fragment key={item.path}>
                <NavDropdownItem
                  key={item.path}
                  label={item.label}
                  path={item.path}
                  onClick={onClose}
                />
                {item.children?.map((child) => (
                  <NavDropdownItem
                    key={child.path}
                    fontWeight="thin"
                    label={child.label}
                    path={child.path}
                    onClick={onClose}
                  />
                ))}
              </Fragment>
            )
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export const Header = ({token}: {token: string}) => {
  const hasIntent = useIsFeatureEnabled('intent');
  const intentModel = useCustomer().intentModels?.at(0);
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();
  const isFreemiumV3Customer = useIsFreemiumV3Customer();

  const markets = useMarkets();
  const marketMenuItems =
    markets.length === 1
      ? [
          {label: 'Account Scoring', path: `/markets/${markets[0].id}`},
          {
            label: 'Serviceable Market',
            path: `/markets/${markets[0].id}/define`,
          },
        ]
      : [];

  return (
    <Box position="fixed" top={0} left={0} right={0} height="50px" zIndex={5}>
      <Flex
        bg="kgray.400"
        color="white"
        align="center"
        h="50"
        gap={2}
        px={4}
        justifyContent="space-between"
      >
        <Box as={Link} flexShrink="0" to="/discover/recommended">
          <Image
            display={{base: 'block', md: 'none'}}
            height="30px"
            src="/Keyplay_Primary_Icon.svg"
          />
          <Image
            display={{base: 'none', md: 'block'}}
            height="30px"
            src="/Keyplay_Primary_Logo_WhiteText.svg"
          />
        </Box>
        <HStack flexShrink="0" ml={{base: 0, md: 20}}>
          {!isFreemiumV3Customer && <NavItem to="/discover">Discover</NavItem>}
          <NavDropdown
            label="Accounts"
            items={[
              {label: 'My Accounts', path: '/accounts'},
              {label: 'Account Lists', path: '/accounts/lists'},
            ]}
          />
          {!isFreeOrListBuilderCustomer && (
            <NavItem to="/agents">Agents</NavItem>
          )}
          <NavDropdown
            label="Models"
            items={[
              {
                label: 'Markets',
                path: '/markets',
                children: marketMenuItems,
              },
              ...(hasIntent || isFreemiumV3Customer
                ? [
                    'divider' as const,
                    {
                      label: 'Intent',
                      path: intentModel
                        ? `/intent/${intentModel.id}`
                        : '/intent',
                    },
                  ]
                : []),
            ]}
          />
        </HStack>
        <Flex justifyContent="end" gap="6">
          {isFreeOrListBuilderCustomer && !isFreemiumV3Customer && (
            <CreditUsage alignContent={'center'} height="32px" />
          )}
          <AppMenu token={token} />
        </Flex>
      </Flex>
    </Box>
  );
};
